import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useCallback } from "react";
import { userPrivilegeService } from "../services/userPrivilegeService";
import { useAuthStore } from "../stores/authStore";
import { useBottomNotificationStore } from "../stores/bottomNotificationStore";

export const userPrivilegeKeys = {
  all: ["userPrivileges"],
  detail: (userId) => [...userPrivilegeKeys.all, userId],
  currentUser: ["userPrivileges", "currentUser"],
};

export const useUserPrivileges = (userId) => {
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useQuery(
    userPrivilegeKeys.detail(userId),
    () => userPrivilegeService.getPrivilegesByUserId(userId),
    {
      enabled: !!userId,
      onError: () => {
        showNotification("Failed to fetch user privileges", "error");
      },
    }
  );
};

export const useSaveUserPrivileges = () => {
  const queryClient = useQueryClient();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation(
    (data) => userPrivilegeService.savePrivileges(data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(userPrivilegeKeys.detail(data.userId));
        queryClient.invalidateQueries(userPrivilegeKeys.currentUser);
        showNotification("Privileges saved successfully", "success");
      },
      onError: (error) => {
        showNotification(error.message || "Failed to save privileges", "error");
      },
    }
  );
};

/**
 * Hook for checking current user's privileges and quick actions.
 * All users (including admins) are subject to privilege checks.
 */
export const useCurrentUserPrivileges = () => {
  const { user, isAuthenticated } = useAuthStore();
  const isAdmin = user?.roles?.includes("ADMIN");
  const userId = user?.id;

  const { data, isLoading } = useQuery(
    userPrivilegeKeys.currentUser,
    () => userPrivilegeService.getPrivilegesByUserId(userId),
    {
      enabled: !!userId && isAuthenticated,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const privilegeKeys = useMemo(() => {
    return data?.privilegeKeys || [];
  }, [data]);

  const quickActions = useMemo(() => {
    return data?.quickActions || [];
  }, [data]);

  const disabledSystemFeatures = useMemo(() => {
    return data?.disabledSystemFeatures || [];
  }, [data]);

  const cancellationGrants = useMemo(() => {
    return data?.cancellationGrants || [];
  }, [data]);

  const hasPrivilege = useCallback(
    (key) => {
      return privilegeKeys.includes(key) && !disabledSystemFeatures.includes(key);
    },
    [privilegeKeys, disabledSystemFeatures]
  );

  const isSuperAdmin = user?.roles?.includes("SUPER_ADMIN");

  const isSystemFeatureEnabled = useCallback(
    (key) => {
      // Super admins bypass the global flag so they can validate a feature
      // before flipping it on for everyone (matches backend behavior).
      if (isSuperAdmin) return true;
      return !disabledSystemFeatures.includes(key);
    },
    [disabledSystemFeatures, isSuperAdmin]
  );

  const canCancelDocument = useCallback(
    (key) => {
      return isSuperAdmin || cancellationGrants.includes(key);
    },
    [isSuperAdmin, cancellationGrants]
  );

  // If not authenticated or no user, privileges are not loading — they simply don't exist
  const effectiveLoading = isAuthenticated && !!userId && isLoading;

  return {
    privilegeKeys,
    hasPrivilege,
    quickActions,
    isAdmin,
    isSuperAdmin,
    isLoading: effectiveLoading,
    disabledSystemFeatures,
    isSystemFeatureEnabled,
    cancellationGrants,
    canCancelDocument,
  };
};

export const useAddQuickAction = () => {
  const queryClient = useQueryClient();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation(
    ({ userId, action }) => userPrivilegeService.addQuickAction(userId, action),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(userPrivilegeKeys.currentUser);
        queryClient.invalidateQueries(userPrivilegeKeys.all);
      },
      onError: () => {
        showNotification("Failed to add shortcut", "error");
      },
    }
  );
};

export const useRemoveQuickAction = () => {
  const queryClient = useQueryClient();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation(
    ({ userId, path }) => userPrivilegeService.removeQuickAction(userId, path),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(userPrivilegeKeys.currentUser);
        queryClient.invalidateQueries(userPrivilegeKeys.all);
      },
      onError: () => {
        showNotification("Failed to remove shortcut", "error");
      },
    }
  );
};
